/* ============================================================
   Care Packs — care-packs.js
   ============================================================ */

(function () {
  "use strict";


  const productCache = {};

  /* WH-172: bounded/retrying fetch, falling back to plain fetch if
     fetch-retry-custom.js didn't load. Cart POSTs go through cartMutate
     (duplicate-safe reconcile before any retry). */
  const ehFetch = (url, opts) =>
    window.EH && window.EH.fetchRetry ? window.EH.fetchRetry(url, opts) : fetch(url, opts);
  const ehCartPost = (url, payload) =>
    window.EH && window.EH.cartMutate
      ? window.EH.cartMutate(url, payload)
      : fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

  /* ── Fetch products for a collection (REST) ── */
  async function fetchProducts(handle, limit) {
    const key = `${handle}:${limit}`;
    if (productCache[key]) return productCache[key];
    try {
      const res = await ehFetch(
        `/collections/${handle}/products.json?limit=${limit}`,
      );
      if (!res.ok) return [];
      const data = await res.json();
      productCache[key] = data.products || [];
      return productCache[key];
    } catch {
      return [];
    }
  }

  /* ── Search for a product by name, with word-fallback retries ── */
  async function searchProduct(name) {
    const words = name.trim().split(/\s+/);
    const queries = [name];
    if (words.length > 3) queries.push(words.slice(-3).join(" "));
    if (words.length > 2) queries.push(words.slice(-2).join(" "));

    for (var qi = 0; qi < queries.length; qi++) {
      var q = queries[qi];
      try {
        var r = await ehFetch(
          "/search/suggest.json?q=" +
            encodeURIComponent(q) +
            "&resources[type]=product&resources[limit]=5",
        );
        if (!r.ok) continue;
        var data = await r.json();
        var suggs =
          (data &&
            data.resources &&
            data.resources.results &&
            data.resources.results.products) ||
          [];
        if (!suggs.length) continue;
        var qLow = q.toLowerCase();
        var match = null;
        for (var si = 0; si < suggs.length; si++) {
          if (suggs[si].title.toLowerCase().indexOf(qLow) !== -1) {
            match = suggs[si];
            break;
          }
        }
        if (!match && qi === queries.length - 1) match = suggs[0];
        if (!match) continue;
        var parts = match.url.split("/products/");
        if (parts.length < 2) continue;
        var handle = parts[1].split("?")[0];
        if (!handle) continue;
        var pr = await ehFetch("/products/" + handle + ".js");
        if (!pr.ok) continue;
        var product = await pr.json();
        console.log("[care-packs] matched:", name, "->", product.title);
        return product;
      } catch (e) {
        console.warn("[care-packs] search error for:", q, e);
      }
    }
    console.warn("[care-packs] not found:", name);
    return null;
  }

  /* ── Fetch component products of a Shopify Bundle ── */
  async function fetchBundleComponentProducts(bundleHandle) {
    var cacheKey = "bundle:" + bundleHandle;
    if (productCache[cacheKey]) return productCache[cacheKey];

    var res;
    try {
      res = await ehFetch("/products/" + bundleHandle + ".js");
    } catch (e) {
      /* WH-172: network failure past all retries must not reject renderPanel
       * unhandled - that leaves the panel stuck on "Loading…". */
      console.warn("[care-packs] bundle fetch failed:", bundleHandle);
      return [];
    }
    if (!res.ok) {
      console.warn("[care-packs] bundle fetch failed:", bundleHandle);
      return [];
    }
    var bundle = await res.json();

    var componentNames = [];

    /* Strategy 1: title "Pack Name - Prod1, Prod2" */
    if (bundle.title && bundle.title.indexOf(" - ") !== -1) {
      var afterDash = bundle.title.split(" - ").slice(1).join(" - ").trim();
      var titleParts = afterDash
        .split(", ")
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean);
      if (titleParts.length >= 2) {
        componentNames = titleParts;
        console.log("[care-packs] from title:", componentNames);
      }
    }

    /* Strategy 2: <li> items in description */
    if (!componentNames.length && bundle.description) {
      try {
        var doc = new DOMParser().parseFromString(
          bundle.description,
          "text/html",
        );
        var items = Array.from(doc.querySelectorAll("li"))
          .map(function (li) {
            return li.textContent.trim();
          })
          .filter(Boolean);
        if (items.length >= 2) {
          componentNames = items;
          console.log("[care-packs] from description:", componentNames);
        }
      } catch (e) {
        console.warn("[care-packs] description parse failed", e);
      }
    }

    /* Strategy 3: option names, strip shared prefix */
    if (!componentNames.length) {
      var cleaned = (bundle.options || [])
        .map(function (opt) {
          var raw = typeof opt === "string" ? opt : opt.name || "";
          return raw
            .replace(/(\s*\([^)]*\))+\s*$/, "")
            .replace(/,.*$/, "")
            .trim();
        })
        .filter(Boolean);
      var prefixLen = 0;
      if (cleaned.length >= 2) {
        var fw = cleaned[0].split(/\s+/);
        for (var i = 0; i < fw.length; i++) {
          var allMatch = cleaned.every(function (c) {
            return (
              (c.split(/\s+/)[i] || "").toLowerCase() === fw[i].toLowerCase()
            );
          });
          if (allMatch) prefixLen++;
          else break;
        }
      }
      componentNames = cleaned.map(function (c) {
        var w = c.split(/\s+/);
        return w.slice(prefixLen).join(" ") || c;
      });
      console.log("[care-packs] from options:", componentNames);
    }

    if (!componentNames.length) {
      console.warn("[care-packs] no components found");
      return [];
    }

    var products = await Promise.all(
      componentNames.map(function (name) {
        return searchProduct(name);
      }),
    );

    var seen = new Set();
    var result = products.filter(function (p) {
      if (!p || seen.has(p.handle)) return false;
      seen.add(p.handle);
      return true;
    });

    console.log(
      "[care-packs] loaded " + result.length + " / " + componentNames.length,
    );
    productCache[cacheKey] = result;
    return result;
  }

  /* ── Format money using Shopify store currency settings ── */
  function formatMoney(cents) {
    var fmt =
      (window.themeVariables && window.themeVariables.moneyFormat) ||
      (window.Shopify && window.Shopify.money_format) ||
      "Rs. {{amount}}";
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, "");
    return fmt
      .replace(/\{\{\s*amount[^}]*\}\}/, amount)
      .replace(/Rs\.?\s*/g, "₹ ");
  }

  /* ── Build HTML for a single product card ── */
  function buildCard(
    p,
    vid,
    price,
    compareHtml,
    discountHtml,
    img,
    rating,
    curatedLabel,
  ) {
    var metaHtml = "";
    if (rating || curatedLabel) {
      metaHtml = '<div class="cp-card__meta">';
      if (rating)
        metaHtml +=
          '<span class="cp-card__star">' +
          rating +
          ' ★</span><span class="cp-card__divider">|</span>';
      if (curatedLabel)
        metaHtml +=
          '<span class="cp-card__curated">' + curatedLabel + "</span>";
      metaHtml += "</div>";
    }
    return `
      <div class="cp-card">
        <a class="cp-card__img-wrap" href="/products/${p.handle}">${img}</a>
        ${metaHtml}
        <a class="cp-card__title" href="/products/${p.handle}">${p.title}</a>
        <div class="cp-card__prices">
          <span class="cp-card__price">${price}</span>
          ${compareHtml}
          ${discountHtml}
        </div>
        <button
          class="cp-card__atc"
          aria-label="Add ${p.title} to cart"
          data-variant-id="${vid}"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 001.99 1.61h9.72a2 2 0 001.99-1.72l1.38-9.28H6"/>
          </svg>
        </button>
      </div>`;
  }

  /* ── Map a product object to card HTML ── */
  function productToCard(p) {
    const variant = p.variants && p.variants[0];
    const vid = variant ? variant.id : "";
    const price = variant ? formatMoney(variant.price) : "";
    const hasDiscount = variant && variant.compare_at_price > variant.price;
    const compare = hasDiscount ? formatMoney(variant.compare_at_price) : "";
    const discPct = hasDiscount
      ? Math.round(
          ((variant.compare_at_price - variant.price) * 100) /
            variant.compare_at_price,
        )
      : 0;

    /* /products/{handle}.js can return images as URL strings or objects with src */
    let imgSrc = "";
    if (p.featured_image) {
      imgSrc =
        typeof p.featured_image === "string"
          ? p.featured_image
          : p.featured_image.src || "";
    } else if (p.images && p.images[0]) {
      imgSrc =
        typeof p.images[0] === "string" ? p.images[0] : p.images[0].src || "";
    }

    const img = imgSrc
      ? `<img src="${imgSrc}" alt="${p.title}" class="cp-card__img" loading="lazy">`
      : "";
    const compareHtml = compare
      ? `<span class="cp-card__compare">${compare}</span>`
      : "";
    const discountHtml =
      discPct > 0
        ? `<span class="cp-card__discount">${discPct}% Off</span>`
        : "";
    var sectionEl = document.querySelector("[data-cp-section]");
    var sectionRating = sectionEl ? sectionEl.dataset.rating || "" : "";

    /* Priority 1: metafield rating + text */
    var displayRating = p._rw_rating || sectionRating;
    var displayLabel = p._rw_text || "";

    /* Priority 2: product tags */
    if (!displayLabel) {
      var tags = (p.tags || []).map(function (t) {
        return t.toLowerCase();
      });
      if (tags.indexOf("bestseller") !== -1) displayLabel = "Bestseller";
      else if (
        tags.some(function (t) {
          return t.indexOf("oncologist") !== -1;
        })
      )
        displayLabel = "Oncologist Curated";
    }

    /* Priority 3: tab name */
    if (!displayLabel) displayLabel = p._tab_label || "";

    return buildCard(
      p,
      vid,
      price,
      compareHtml,
      discountHtml,
      img,
      displayRating,
      displayLabel,
    );
  }

  /* ── Render product cards into a panel grid ── */
  async function renderPanel(panel, limit) {
    if (panel.dataset.loaded) return;
    panel.dataset.loaded = "true";

    const collectionHandle = panel.dataset.collection;
    const bundleHandle = panel.dataset.bundleProduct;
    const grid = panel.querySelector(".cp-panel__grid");
    if (!grid) return;

    grid.innerHTML = '<p class="cp-empty">Loading…</p>';

    let products = [];

    /* Bundle takes priority over collection */
    if (bundleHandle) {
      const componentHandles = (panel.dataset.componentHandles || "")
        .split(",")
        .map(function (h) { return h.trim(); })
        .filter(Boolean);

      if (componentHandles.length) {
        const fetched = await Promise.all(
          componentHandles.map(async function (h) {
            try {
              const r = await ehFetch("/products/" + h + ".js");
              return r.ok ? await r.json() : null;
            } catch (e) {
              return null;
            }
          }),
        );
        products = fetched.filter(Boolean);
      }

      if (!products.length) {
        products = await fetchBundleComponentProducts(bundleHandle);
      }

      if (!products.length) {
        try {
          const res = await ehFetch(`/products/${bundleHandle}.js`);
          if (res.ok) products = [await res.json()];
        } catch {}
      }
    }

    /* Collection fallback */
    if (!products.length && collectionHandle) {
      products = await fetchProducts(collectionHandle, limit);
    }

    if (!products.length) {
      grid.innerHTML = '<p class="cp-empty">No products found.</p>';
      return;
    }

    /* ── Read metafield data from pre-rendered JSON script tag ── */
    var tabLabel = panel.dataset.tabLabel || "";
    var metaStore = {};
    var metaEl = panel.querySelector(".cp-meta-data");
    if (metaEl) {
      try {
        metaStore = JSON.parse(metaEl.textContent);
        console.log("[care-packs] metaStore:", metaStore);
      } catch (e) {
        console.warn("[care-packs] meta parse error:", e);
      }
    } else {
      console.warn("[care-packs] no .cp-meta-data found in panel:", panel.dataset.panel);
    }
    products = products.map(function (p) {
      var meta = metaStore[p.handle] || {};
      p._rw_rating = meta.rating || "";
      p._rw_text = meta.text || "";
      p._tab_label = tabLabel;
      console.log("[care-packs] product:", p.handle, "→ _rw_rating:", p._rw_rating);
      return p;
    });

    grid.innerHTML = products.map(productToCard).join("");
    grid.scrollLeft = 0;
    setTimeout(function() { grid.scrollLeft = 0; }, 50);
    grid.dataset.count = products.length;
    panel.dataset.count = products.length;
    if (products.length > 3) grid.classList.add("cp-panel__grid--scroll");

    /* Update footer total price */
    var totalEl = panel.querySelector('.cp-panel__total');
    if (totalEl) {
      var totalCents = products.reduce(function(sum, p) {
        return sum + ((p.variants && p.variants[0] && p.variants[0].price) || 0);
      }, 0);
      totalEl.innerHTML = 'Buy all ' + products.length + ' for <span class="cp-panel__total-price">' + formatMoney(totalCents) + '</span>';
    }

    /* wire up individual ATC buttons */
    grid.querySelectorAll(".cp-card__atc").forEach((btn) => {
      btn.addEventListener("click", () =>
        addToCart([btn.dataset.variantId], btn),
      );
    });
  }

  /* ── Cart drawer section ID helper (matches Prestige extractSectionId) ── */
  function getDrawerSectionId() {
    const el = document.querySelector("cart-drawer");
    if (!el) return null;
    const section = el.closest(".shopify-section");
    return section ? section.id.replace("shopify-section-", "") : null;
  }

  /* ── Add variants to cart ── */
  async function addToCart(variantIds, triggerEl) {
    if (triggerEl) triggerEl.disabled = true;

    const items = variantIds
      .filter(Boolean)
      .map((id) => ({ id: Number(id), quantity: 1 }));
    if (!items.length) return;

    try {
      const sectionId = getDrawerSectionId();
      const body = { items };
      if (sectionId) body.sections = sectionId;

      const res = await ehCartPost("/cart/add.js", body);
      if (!res.ok) throw new Error("cart error");
      const addResp = await res.json();

      /* update cart count + open Prestige cart drawer */
      const countRes = await ehFetch("/cart.js");
      const cart = await countRes.json();
      if (addResp.sections) cart.sections = addResp.sections;

      document.querySelectorAll("[data-cart-count]").forEach((el) => {
        el.textContent = cart.item_count;
      });
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: { baseEvent: "variant:add", cart },
        }),
      );

      /* ── Analytics ── */
      if (triggerEl && window.fireAddToCartEvent) {
        var card = triggerEl.closest('.cp-card');
        if (card) {
          /* Single card ATC */
          var title = ((card.querySelector('.cp-card__title') || {}).textContent || '').trim();
          var priceEl = card.querySelector('.cp-card__price');
          var priceVal = priceEl ? parseFloat(priceEl.textContent.replace(/[^\d.]/g, '')) || 0 : 0;
          window.fireAddToCartEvent(String(variantIds[0] || ''), title, priceVal, 1);
        } else {
          /* Buy All — collect all cards in the panel */
          var panel = triggerEl.closest('.cp-panel');
          var allCards = panel ? panel.querySelectorAll('.cp-card') : [];
          var totalValue = 0;
          var firstTitle = '';
          allCards.forEach(function(c, i) {
            var t = ((c.querySelector('.cp-card__title') || {}).textContent || '').trim();
            var pe = c.querySelector('.cp-card__price');
            var pv = pe ? parseFloat(pe.textContent.replace(/[^\d.]/g, '')) || 0 : 0;
            totalValue += pv;
            if (i === 0) firstTitle = t;
          });
          /* Fire GA4 with all items, Meta with total value */
          if (window.gtag) {
            var gaItems = [];
            allCards.forEach(function(c, i) {
              var t = ((c.querySelector('.cp-card__title') || {}).textContent || '').trim();
              var pe = c.querySelector('.cp-card__price');
              var pv = pe ? parseFloat(pe.textContent.replace(/[^\d.]/g, '')) || 0 : 0;
              gaItems.push({ item_id: String(variantIds[i] || ''), item_name: t, price: pv, quantity: 1 });
            });
            gtag('event', 'add_to_cart', { currency: 'INR', value: totalValue, items: gaItems });
          }
          if (window.fbq) {
            fbq('track', 'AddToCart', { content_ids: variantIds.map(String), content_type: 'product', value: totalValue, currency: 'INR' });
          }
        }
      }

      /* flash ✓ on button */
      if (triggerEl) {
        const orig = triggerEl.innerHTML;
        triggerEl.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
        setTimeout(() => {
          triggerEl.innerHTML = orig;
          triggerEl.disabled = false;
        }, 1200);
      }
    } catch (err) {
      console.error("[care-packs] addToCart error:", err);
      if (triggerEl) triggerEl.disabled = false;
    }
  }

  /* ── Tab switching ── */
  function initTabs(section, limit) {
    const tabs = section.querySelectorAll(".cp-tab");
    const panels = section.querySelectorAll(".cp-panel");

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const target = tab.dataset.tab;

        tabs.forEach((t) => t.classList.toggle("cp-tab--active", t === tab));
        panels.forEach((p) => {
          const active = p.dataset.panel === target;
          p.classList.toggle("cp-panel--active", active);
          if (active) {
            var g = p.querySelector(".cp-panel__grid");
            if (g) g.scrollLeft = 0;
            renderPanel(p, limit);
          }
        });
      });
    });

    /* load first active panel immediately */
    const firstPanel = section.querySelector(".cp-panel--active");
    if (firstPanel) renderPanel(firstPanel, limit);
  }

  /* ── Buy All button ── */
  function initBuyAll(section) {
    section.querySelectorAll(".cp-panel__buy-btn").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const panel = btn.closest(".cp-panel");
        const bundleVariantId = panel.dataset.bundleVariant;
        if (bundleVariantId) {
          await addToCart([bundleVariantId], btn);
        } else {
          const variantIds = [...panel.querySelectorAll(".cp-card__atc")]
            .map((b) => b.dataset.variantId)
            .filter(Boolean);
          await addToCart(variantIds, btn);
        }
      });
    });
  }

  /* ── Ask Expert form ── */
  function initAskForm(section) {
    const form = section.querySelector(".cp-ask__form");
    if (!form) return;
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const input = form.querySelector(".cp-ask__input");
      const question = input ? input.value.trim() : "";
      /*if (!question) return;*/
      const base = form.getAttribute("action").split("?")[0];
      window.location.href = base 
      + "?ask=" + encodeURIComponent(question)
      + '&src=' + encodeURIComponent(window.location.pathname + window.location.search)
      + '&src_title=' + encodeURIComponent(document.title);
    });
  }

  /* ── Drag-to-scroll + progress bar for product grids ── */
  function initGridScroll(section) {
    section.querySelectorAll(".cp-panel__grid").forEach(function (grid) {
      var panel = grid.closest(".cp-panel");
      var thumb = panel ? panel.querySelector(".cp-progress__thumb") : null;
      var bar = panel ? panel.querySelector(".cp-progress__bar") : null;

      function updateProgress() {
        if (!thumb || !bar) return;
        var maxScroll = grid.scrollWidth - grid.clientWidth;
        if (maxScroll <= 0) {
          thumb.style.width = "100%";
          thumb.style.left = "0";
          return;
        }
        var barW = bar.offsetWidth;
        var ratio = grid.clientWidth / grid.scrollWidth;
        var thumbW = Math.max(20, Math.round(barW * ratio));
        var thumbL = Math.round(
          (barW - thumbW) * (grid.scrollLeft / maxScroll),
        );
        thumb.style.width = thumbW + "px";
        thumb.style.left = thumbL + "px";
      }

      grid.addEventListener("scroll", updateProgress, { passive: true });
      var ro = new ResizeObserver(updateProgress);
      ro.observe(grid);

      var isDragging = false,
        startX = 0,
        scrollLeft = 0;
      grid.addEventListener("mousedown", function (e) {
        isDragging = true;
        startX = e.pageX - grid.offsetLeft;
        scrollLeft = grid.scrollLeft;
        grid.classList.add("is-dragging");
      });
      grid.addEventListener("mouseleave", function () {
        isDragging = false;
        grid.classList.remove("is-dragging");
      });
      grid.addEventListener("mouseup", function () {
        isDragging = false;
        grid.classList.remove("is-dragging");
      });
      grid.addEventListener("mousemove", function (e) {
        if (!isDragging) return;
        e.preventDefault();
        var x = e.pageX - grid.offsetLeft;
        grid.scrollLeft = scrollLeft - (x - startX) * 1.5;
      });
    });
  }

  /* ── Boot ── */
  document.querySelectorAll("[data-cp-section]").forEach((section) => {
    const limit = parseInt(section.dataset.limit, 10) || 6;
    initTabs(section, limit);
    initBuyAll(section);
    initAskForm(section);
    initGridScroll(section);
  });
})();
